import { create } from "zustand";
import { persist } from "zustand/middleware";

import {
  fetchAdminEventData,
  fetchEvents,
  createEvent,
  type EventSubmissionData,
  type EventCompositeData,
  type EventQueueJobData,
  type EventPrintQueueData,
  type EventData,
  type DataArchive,
} from "@/shared/api/AdminApi";

interface AdminEventDataState {
  // Event selection
  selectedEventId: number | null;
  events: EventData[];

  // Data tables
  submissions: Record<number, EventSubmissionData>;
  composites: Record<number, EventCompositeData>;
  queueJobs: Record<string, EventQueueJobData>;
  dataArchives: Record<number, DataArchive>;
  printQueue: Record<number, EventPrintQueueData>;

  // Metadata
  lastFetchTimestamp: number | null;

  // Loading states
  isLoading: boolean;
  error: string | null;

  // Polling state
  pollingTimeoutId: ReturnType<typeof setTimeout> | null;

  // Event actions
  setSelectedEventId: (eventId: number | null) => void;
  fetchEventList: () => Promise<void>;
  archiveEvent: (name: string) => Promise<EventData>;

  // Data actions
  fetchChanges: (since?: number) => Promise<void>;
  clear: () => void;
  startPolling: () => Promise<void>;
  stopPolling: () => void;

  // Getters
  getSubmissionById: (submissionId: number) => EventSubmissionData | undefined;
  getCompositeById: (compositeId: number) => EventCompositeData | undefined;
  getQueueJobById: (jobId: string) => EventQueueJobData | undefined;
  getDataArchiveById: (id: number) => DataArchive | undefined;
  removeDataArchive: (id: number) => void;
  getPrintQueueById: (printQueueId: number) => EventPrintQueueData | undefined;

  // Filtered getters
  getCompositesBySubmissionId: (submissionId: number) => EventCompositeData[];
}

export const useAdminEventDataStore = create<AdminEventDataState>()(
  persist(
    (set, get) => ({
      // Initial state
      selectedEventId: null,
      events: [],
      submissions: {},
      composites: {},
      queueJobs: {},
      dataArchives: {},
      printQueue: {},

      lastFetchTimestamp: null,

      isLoading: false,
      error: null,
      pollingTimeoutId: null,

      // Event selection
      setSelectedEventId: (eventId) => {
        set({ selectedEventId: eventId });
        get().clear();
        get().fetchChanges();
      },

      // Fetch event list
      fetchEventList: async () => {
        try {
          const response = await fetchEvents();
          set({ events: response.events });
        } catch (error) {
          console.error("Failed to fetch events:", error);
        }
      },

      // Archive current event
      archiveEvent: async (name: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await createEvent(name);
          if (!response.event) {
            throw new Error("No event returned from archive");
          }
          set((state) => ({
            events: [response.event!, ...state.events],
            isLoading: false,
          }));
          get().clear();
          return response.event;
        } catch (error) {
          set({
            error:
              error instanceof Error
                ? error.message
                : "Failed to archive event",
            isLoading: false,
          });
          throw error;
        }
      },

      // Fetch only changes since last update
      fetchChanges: async () => {
        const state = get();
        const changesSince = state.lastFetchTimestamp ?? 0;
        const selectedEventId = state.selectedEventId;

        set({ isLoading: true, error: null });
        try {
          const response = await fetchAdminEventData(
            changesSince,
            selectedEventId,
          );
          if (get().pollingTimeoutId === null) return;
          set({
            submissions: {
              ...state.submissions,
              ...Object.fromEntries(
                response.submissions.map((submission) => [
                  submission.submission_id,
                  submission,
                ]),
              ),
            },
            composites: {
              ...state.composites,
              ...Object.fromEntries(
                response.composites.map((c) => [c.composite_id, c]),
              ),
            },
            queueJobs: {
              ...state.queueJobs,
              ...Object.fromEntries(
                response.queue_jobs.map((j) => [j.queue_job_id, j]),
              ),
            },
            dataArchives: {
              ...state.dataArchives,
              ...Object.fromEntries(
                response.data_archives.map((a) => [a.data_archive_id, a]),
              ),
            },
            printQueue: {
              ...state.printQueue,
              ...Object.fromEntries(
                response.print_queue.map((p) => [p.print_queue_id, p]),
              ),
            },
            lastFetchTimestamp: response.metadata.timestamp,
            isLoading: false,
          });
        } catch (error) {
          set({
            error:
              error instanceof Error
                ? error.message
                : "Failed to fetch changes",
            isLoading: false,
          });
        }
      },

      // Clear all data
      clear: () => {
        set({
          submissions: {},
          composites: {},
          queueJobs: {},
          dataArchives: {},
          printQueue: {},
          lastFetchTimestamp: null,
          error: null,
        });
      },

      // Start automatic polling
      startPolling: async () => {
        if (get().pollingTimeoutId !== null) {
          return;
        }

        const poll = async () => {
          await get()
            .fetchChanges()
            .catch((error) => {
              console.error("Failed to fetch changes during polling:", error);
            });

          if (get().pollingTimeoutId !== null) {
            set({ pollingTimeoutId: setTimeout(poll, 5000) });
          }
        };

        set({ pollingTimeoutId: setTimeout(poll, 5000) });

        get()
          .fetchChanges()
          .catch((error) => {
            console.error("Failed to fetch changes during polling:", error);
          });
      },

      // Stop automatic polling
      stopPolling: () => {
        const { pollingTimeoutId } = get();
        if (pollingTimeoutId !== null) {
          clearTimeout(pollingTimeoutId);
          set({ pollingTimeoutId: null });
        }
      },

      // Getters
      getSubmissionById: (submissionId) => get().submissions[submissionId],
      getCompositeById: (compositeId) => get().composites[compositeId],
      getQueueJobById: (jobId) => get().queueJobs[jobId],
      getDataArchiveById: (id) => get().dataArchives[id],
      getPrintQueueById: (id) => get().printQueue[id],
      removeDataArchive: (id) => {
        set((state) => {
          const next = { ...state.dataArchives };
          delete next[id];
          return { dataArchives: next };
        });
      },

      // Filtered getters
      getCompositesBySubmissionId: (submissionId) => {
        return Object.values(get().composites).filter(
          (comp) => comp.submission_id === submissionId,
        );
      },
    }),
    {
      name: "admin-event-data-store",
      partialize: (state) => ({ selectedEventId: state.selectedEventId }),
    },
  ),
);
